import { useEffect, type PointerEvent } from 'react';
import { engine } from '../engine/engine';
import styles from './TouchControls.module.css';

export interface TouchControlState {
  left: boolean;
  right: boolean;
  plunger: boolean;
  nudge: boolean;
}

type NudgeDirection = 'left' | 'up' | 'right';
type HapticNavigator = { vibrate?: (pattern: VibratePattern) => boolean };

export function pulseHapticSafely(
  duration = 8,
  navigatorLike: HapticNavigator | null = typeof navigator === 'undefined' ? null : navigator,
): void {
  if (typeof navigatorLike?.vibrate !== 'function') return;
  try {
    navigatorLike.vibrate(duration);
  } catch {
    // Haptics are optional. Some embedded/privacy-restricted browsers expose
    // vibrate() but throw when called; touch controls must still latch normally.
  }
}

export function resetTouchControlState(state: TouchControlState): void {
  state.left = false;
  state.right = false;
  state.plunger = false;
  state.nudge = false;
}

export function runTouchNudge(
  state: Pick<TouchControlState, 'nudge'>,
  performNudge: () => void,
): void {
  state.nudge = true;
  try {
    performNudge();
  } finally {
    // Keep the transient touch edge from getting stuck if a nudge callback
    // fails. A latched nudge can otherwise leak into subsequent input frames.
    state.nudge = false;
  }
}

function TouchButton({
  label,
  action,
  state,
  className = '',
}: {
  label: string;
  action: 'left' | 'right' | 'plunger';
  state: React.MutableRefObject<TouchControlState>;
  className?: string;
}) {
  const setPressed = (event: PointerEvent<HTMLButtonElement>, pressed: boolean) => {
    event.preventDefault();
    if (pressed) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture can fail when the pointer was already released or
        // transferred by the browser. The control state below remains valid.
        void error;
      }
      pulseHapticSafely();
    }
    state.current[action] = pressed;
  };

  return (
    <button
      className={`${styles.control} ${className}`}
      onPointerDown={(event) => setPressed(event, true)}
      onPointerUp={(event) => setPressed(event, false)}
      onPointerCancel={(event) => setPressed(event, false)}
      onLostPointerCapture={(event) => setPressed(event, false)}
      onContextMenu={(event) => event.preventDefault()}
      aria-label={label}
    >
      {label}
    </button>
  );
}

export default function TouchControls({
  state,
  nudgesRemaining,
  onNudge,
}: {
  state: React.MutableRefObject<TouchControlState>;
  nudgesRemaining: number;
  onNudge: () => void;
}) {
  useEffect(() => () => {
    // React/browser teardown is not guaranteed to deliver pointerup or lost
    // pointer capture first. Clear every shared touch latch so view changes,
    // remounts, and navigation cannot leave a flipper or plunger held forever.
    resetTouchControlState(state.current);
  }, [state]);

  const nudge = (event: PointerEvent<HTMLButtonElement>, direction: NudgeDirection) => {
    event.preventDefault();
    if (nudgesRemaining <= 0) return;
    pulseHapticSafely(12);
    runTouchNudge(state.current, () => {
      if (direction === 'left') engine.nudge({ x: -1, y: 0 });
      else if (direction === 'right') engine.nudge({ x: 1, y: 0 });
      else onNudge();
    });
  };

  return (
    <div className={styles.controls} aria-label="Touch pinball controls">
      <TouchButton label="FLIP L" action="left" state={state} className={styles.flipper} />

      <div className={styles.centerControls}>
        <div className={styles.nudgePad} aria-label={`${nudgesRemaining} nudges remaining`}>
          <button
            className={styles.nudge}
            onPointerDown={(event) => nudge(event, 'left')}
            onContextMenu={(event) => event.preventDefault()}
            disabled={nudgesRemaining <= 0}
            aria-label={`Nudge left, ${nudgesRemaining} remaining`}
          >
            ←
          </button>
          <button
            className={styles.nudge}
            onPointerDown={(event) => nudge(event, 'up')}
            onContextMenu={(event) => event.preventDefault()}
            disabled={nudgesRemaining <= 0}
            aria-label={`Nudge up, ${nudgesRemaining} remaining`}
          >
            ↑
          </button>
          <button
            className={styles.nudge}
            onPointerDown={(event) => nudge(event, 'right')}
            onContextMenu={(event) => event.preventDefault()}
            disabled={nudgesRemaining <= 0}
            aria-label={`Nudge right, ${nudgesRemaining} remaining`}
          >
            →
          </button>
        </div>
        <TouchButton label="HOLD · PLUNGE" action="plunger" state={state} className={styles.plunger} />
      </div>

      <TouchButton label="FLIP R" action="right" state={state} className={styles.flipper} />
    </div>
  );
}
